import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { Seo } from "@/components/Seo";
import { ARTICLES } from "@/data/articles";

const SITE = "https://gestimmony.fr";

export default function ResourcesPage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Ressources Gestimmony",
    description: "Guides pratiques et fiscaux pour les propriétaires bailleurs.",
    url: `${SITE}/ressources`,
    blogPost: ARTICLES.map((a) => ({
      "@type": "BlogPosting",
      headline: a.title,
      datePublished: a.date,
      url: `${SITE}/ressources/${a.slug}`,
    })),
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="resources-page">
      <Seo
        title="Ressources pour propriétaires bailleurs — Guides loyers & fiscalité | Gestimmony"
        description="Révision du loyer avec l'IRL, déclaration 2044, quittances : nos guides pratiques pour gérer vos locations en toute sérénité."
        path="/ressources"
        jsonLd={jsonLd}
      />

      <header className="border-b border-stone-200 bg-white/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="resources-logo">
            <LogoMark className="h-8" />
            <span className="font-heading font-bold">Gestimmony</span>
          </Link>
          <div className="flex items-center gap-5 text-sm">
            <Link to="/tarifs" className="text-slate-600 hover:text-[hsl(var(--primary))]">Tarifs</Link>
            <Link to="/register" className="btn-primary text-sm" data-testid="resources-cta-register">Créer un compte</Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-2xl">
          <div className="inline-flex items-center rounded-full bg-teal-50 text-teal-800 text-xs font-medium px-3 py-1 mb-4">
            Ressources & guides
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900" data-testid="resources-title">
            Tout pour gérer vos locations sereinement
          </h1>
          <p className="mt-4 text-slate-600 text-base leading-relaxed">
            Des guides clairs et à jour sur la révision des loyers, la fiscalité foncière et vos
            obligations de bailleur — écrits pour les propriétaires, pas pour les experts-comptables.
          </p>
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {ARTICLES.map((a) => (
            <Link
              key={a.slug}
              to={`/ressources/${a.slug}`}
              data-testid={`article-card-${a.slug}`}
              className="group flex flex-col rounded-2xl border border-stone-200 bg-white overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all"
            >
              <div className="aspect-[16/10] overflow-hidden bg-stone-100">
                <img
                  src={a.cover}
                  alt={a.title}
                  loading="lazy"
                  className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="flex flex-col flex-1 p-5">
                <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                  <span className="font-medium text-teal-700">{a.category}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {a.readTime}</span>
                </div>
                <h2 className="font-heading font-semibold text-slate-900 text-lg leading-snug group-hover:text-[hsl(var(--primary))] transition-colors">
                  {a.title}
                </h2>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{a.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--primary))]">
                  Lire l'article <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-16 rounded-2xl bg-[hsl(var(--primary))] text-white p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-2xl font-bold">Passez de la théorie à la pratique</h3>
            <p className="mt-2 text-white/80 max-w-xl">
              Révision IRL automatique, quittances en un clic, agrégation de votre 2044 : Gestimmony
              applique tout ça pour vous. Sans engagement en mensuel.
            </p>
          </div>
          <Link to="/register" data-testid="resources-footer-cta" className="shrink-0 inline-flex items-center gap-2 bg-white text-[hsl(var(--primary))] font-semibold rounded-full px-6 py-3 hover:bg-stone-100 transition-colors">
            Créer mon compte <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-10">
          <Link to="/" className="text-sm text-slate-600 hover:text-[hsl(var(--primary))] inline-flex items-center gap-1.5">
            <ArrowLeft className="h-4 w-4" /> Retour à l'accueil
          </Link>
        </div>
      </main>
    </div>
  );
}
